import {
  DateFormat,
  formatDateTime,
  getDateDiffDays,
  hasDateTimeWindow,
} from '@project44/ui-utils';

interface DateTimeWindowProps {
  displayTimezoneAbbreviation?: boolean;
  displayTimeWindowOnly?: boolean; // if `true` omits the month day and middle dot
  endDateTime?: Date | string;
  startDateTime?: Date | string;
  timeZone?: string;
}

/**
 * Displays formatted date with optional time window. To display without time window either omit `endDateTime` or
 * pass the same date to `startDateTime` and `endDateTime`.
 * Return formats:
 * - no window: `Jun 04 · 23:48`
 * - with window: `Jun 04 · 23:48-23:55`
 * - with window > 0 days: `Jun 04 · 23:48-01:55+1`
 * - with no `startDateTime`: `-`
 * - with `displayTimeWindowOnly: true`: `23:48-23:55`
 */
export const DateTimeWindow = ({
  displayTimezoneAbbreviation = false,
  displayTimeWindowOnly = false,
  endDateTime = '',
  startDateTime = '',
  timeZone,
}: DateTimeWindowProps) => {
  // `formatDateTime` already has a default timeZone so only add it here if we have a value (override)
  const hasTimeWindow = hasDateTimeWindow({
    startDateTime,
    endDateTime,
  });

  const diffDays = hasTimeWindow
    ? getDateDiffDays({ startDateTime, endDateTime })
    : 0;

  const dateTimeTimezone = timeZone ? { timeZone } : {};

  const monthDay = !displayTimeWindowOnly
    ? formatDateTime({
        dateTime: startDateTime,
        dateFormat: DateFormat.shortMonthDay,
        ...dateTimeTimezone,
      })
    : '';

  const startHourMinute = formatDateTime({
    dateTime: startDateTime,
    dateFormat: DateFormat.hourMinute,
    ...dateTimeTimezone,
  });

  const endHourMinute = formatDateTime({
    dateTime: endDateTime,
    dateFormat: DateFormat.hourMinute,
    ...dateTimeTimezone,
  });

  const timezoneAbbreviation = displayTimezoneAbbreviation
    ? formatDateTime({
        dateTime: new Date(),
        dateFormat: DateFormat.timezone,
        ...dateTimeTimezone,
      })
    : '';

  if (!startDateTime) {
    return <span data-testid="date-time-no-date">-</span>;
  }

  return hasTimeWindow ? (
    <span data-testid="date-time-with-window">
      {monthDay && (
        <span data-testid="with-window-month-day">{monthDay} &middot; </span>
      )}
      {startHourMinute}-{endHourMinute}
      {diffDays > 0 && (
        <span
          data-testid="diff-days"
          style={{
            fontSize: 10,
            verticalAlign: 'text-bottom',
          }}
        >
          {`+${diffDays}`}
        </span>
      )}
      {timezoneAbbreviation && (
        <span
          data-testid="date-time-timezone"
          style={{
            paddingRight: `${diffDays > 0 ? '8px' : 0}`,
          }}
        >
          {' '}
          {timezoneAbbreviation}
        </span>
      )}
    </span>
  ) : (
    <span data-testid="date-time-no-window">
      {monthDay && (
        <span data-testid="no-window-month-day">{monthDay} &middot; </span>
      )}{' '}
      {startHourMinute} {timezoneAbbreviation}
    </span>
  );
};

export default DateTimeWindow;
